import React, { useEffect, useMemo, useState } from "react";
import PosCustomIcon from "../../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import "./ComplimentaryProductList.css";
import vagIcon from "../../../../../../assets/images/veg-new.png";
import nonVegIcon from "../../../../../../assets/images/non-veg-new.png";
import { MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import PosCustomCheckBox from "../../../../../../customComponents/PosCustomCheckBox/PosCustomCheckBox";
import { getStoreCurrencySymbol } from "../../../../../../utils/currency";

const ComplimentaryProductList = ({
  products,
  selectedIds,
  setSelectedIds,
  getTotalKOTs,
  complimentaryProductPayload,
  setComplimentaryProductPayload,
  markWholeCartComplimentary,
}) => {
  const [complimentaryQuantities, setComplimentaryQuantities] = useState(() => {
    const initialQuantities = {};
    products?.forEach((item) => {
      initialQuantities[item?.cartProductId] = 1;
    });
    return initialQuantities;
  });

  /*
   *Getting values from local storage
   */
  const locId = localStorage.getItem("locId");
  const cartIdFromLocal = localStorage.getItem("CartId");
  const retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));

  /*
   *Getting non complementary products from the product list
   */
  const nonComplementaryProducts = useMemo(
    () =>
      products?.filter(
        (ele) => ele?.inventoryStatus !== "COMPLEMENTARY" && ele?.productStatus !== "RETURNED"
      ) || [],
    [products]
  );

  /*
   *Checking if all produts are markes as complimentor to show on the UI
   */
  const allComplementary = useMemo(
    () => products?.length > 0 && nonComplementaryProducts?.length === 0,
    [products, nonComplementaryProducts]
  );

  //Handling Increase quantity
  const handleIncreaseQty = (cartProductId, maxQty) => {
    const current = complimentaryQuantities[cartProductId] || 1;
    /*
     *Checking if the current quantity do not exceeding the max quantity
     */
    if (current >= maxQty) {
      return;
    }
    /*
     *Storing updated quantity
     */
    setComplimentaryQuantities((prev) => ({
      ...prev,
      [cartProductId]: current + 1,
    }));
  };

  const handleDecreaseQty = (cartProductId) => {
    const current = complimentaryQuantities[cartProductId] || 1;
    /*
     *Checking quantity should not decresing below 1
     */
    if (current <= 1) {
      return;
    }
    /*
     *Storing updated quantity
     */
    setComplimentaryQuantities((prev) => ({
      ...prev,
      [cartProductId]: current - 1,
    }));
  };

  /*
   *Function to handle quantity using the input field
   */
  const handleQuantityInput = (e, cartProductId, maxQty) => {
    let value = e.target.value;

    // Allow temporary empty input while typing
    if (value === "") {
      setComplimentaryQuantities((prev) => ({ ...prev, [cartProductId]: "" }));
      return;
    }

    value = Number(value);

    // Handle invalid, zero, or negative inputs
    if (isNaN(value) || value <= 0) {
      value = 1;
    }
    // Handle greater than allowed quantity
    else if (value > maxQty) {
      value = maxQty;
    }

    setComplimentaryQuantities((prev) => ({ ...prev, [cartProductId]: value }));
  };

  /*
   *Handle Selection of individaul items
   */
  const handleCheck = (e) => {
    const { value, checked } = e.target;
    setSelectedIds((prev) => (checked ? [...prev, value] : prev.filter((id) => id !== value)));
  };

  /*
   *determines whether all NON-complementary products are selected AND have maximum quantities.
   */
  const allSelected = useMemo(
    () => {
      if (nonComplementaryProducts?.length === 0 || selectedIds?.length !== nonComplementaryProducts?.length) {
        return false;
      }
      return nonComplementaryProducts?.every(
        (product) => (complimentaryQuantities[product?.cartProductId] || 1) === product?.quantity
      );
    },
    [nonComplementaryProducts, selectedIds, complimentaryQuantities]
  );

  /*
   *Handle Selection of all prodcuts at once
   */
  const handleSelectAll = (e) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedIds(nonComplementaryProducts?.map((item) => item?.cartProductId));
      const maxQuantities = {};
      nonComplementaryProducts?.forEach((item) => {
        maxQuantities[item?.cartProductId] = item?.quantity;
      });
      setComplimentaryQuantities(maxQuantities);
    } else {
      setSelectedIds([]);
      const initialQuantities = {};
      nonComplementaryProducts?.forEach((item) => {
        initialQuantities[item?.cartProductId] = 1;
      });
      setComplimentaryQuantities(initialQuantities);
    }
  };

  /*
   *Creating API for individaul selected items
   */
  useEffect(() => {
    /*
     *Checking if all cart is marked as complimentary
     */
    if (markWholeCartComplimentary) {
      return;
    }

    /*
     *If non of the product is selected
     */
    if (!selectedIds?.length) {
      setComplimentaryProductPayload({
        locationId: locId,
        cartId: cartIdFromLocal,
        cartProducts: {},
        markWholeCart: false,
        updatedBy: retailUserLocalStorageData?.uidx,
        updatedByName: retailUserLocalStorageData?.firstName,
      });
      return;
    }

    /*
     *When all products are selected (All ticked or every product checked manually),
     *send the whole cart payload without the cartProducts field
     */
    if (allSelected) {
      setComplimentaryProductPayload({
        locationId: locId,
        cartId: cartIdFromLocal,
        markWholeCart: true,
        updatedBy: retailUserLocalStorageData?.uidx,
        updatedByName: retailUserLocalStorageData?.firstName,
      });
      return;
    }

    const cartProducts = {};

    selectedIds.forEach((id) => {
      const qty = complimentaryQuantities[id] || 1;
      cartProducts[id] = qty;
    });

    const payload = {
      locationId: locId,
      cartId: cartIdFromLocal,
      cartProducts,
      markWholeCart: false,
      updatedBy: retailUserLocalStorageData?.uidx,
      updatedByName: retailUserLocalStorageData?.firstName,
    };

    setComplimentaryProductPayload(payload);
  }, [selectedIds, complimentaryQuantities, setComplimentaryProductPayload, allSelected]);

  useEffect(() => {
    // Clear all selected IDs
    setSelectedIds([]);

    // Reset all complimentary quantities to 1
    const initialQuantities = {};
    products?.forEach((item) => {
      initialQuantities[item?.cartProductId] = 1;
    });
    setComplimentaryQuantities(initialQuantities);
  }, [products]);

  /*
   *Visually updating the prodcts quantity to max when Mark whole cart is selected
   */
  useEffect(() => {
    if (markWholeCartComplimentary) {
      const updatedQuantities = {};
      products?.forEach((item) => (updatedQuantities[item?.cartProductId] = item?.quantity));
      setComplimentaryQuantities(updatedQuantities);
    }
  }, [markWholeCartComplimentary, products]);

  return (
    <div className="complimentaryProductListContainer">
      <div className="comp-option-container">
        <PosCustomCheckBox
          variant="white-d"
          id="select-all-checkbox"
          name="select-all"
          value="all"
          label="All"
          onCheck={handleSelectAll}
          checkedValue={allSelected ? ["all"] : []}
          height="18px"
          radius="4px"
          defaultIcon={false}
          transparent={true}
          iconOnRight
          checkBoxColor={"#0860E6"}
          checkMarkColor={"#ffffff"}
          padding="0px"
          disabled={markWholeCartComplimentary}
        />
      </div>

      <hr className="comp-upper-boder" />

      {allComplementary ? (
        <div className="allCompMessage">
          <p>All items are already marked as complementary</p>
        </div>
      ) : (
        <>
          {nonComplementaryProducts?.map((ele, idx) => {
            const isSelected = selectedIds.includes(ele?.cartProductId);
            return (
              <div key={idx} className="complimentaryProductList">
                <div className="compUpperContainer">
                  <div className="compFoodTypeContainer">
                    <PosCustomCheckBox
                      variant="white-d"
                      id={`comp-check-${ele?.cartProductId}`}
                      name="complimentary-product"
                      value={ele?.cartProductId}
                      checkedValue={markWholeCartComplimentary ? [ele?.cartProductId] : selectedIds}
                      onCheck={handleCheck}
                      width="18px"
                      height="18px"
                      radius="4px"
                      defaultIcon={false}
                      transparent={true}
                      iconOnLeft
                      checkBoxColor={"#0860E6"}
                      checkMarkColor={"#ffffff"}
                      padding="0px"
                      disabled={markWholeCartComplimentary}
                    />
                    <div style={{ width: "14px", height: "14px", marginTop: "2px", flexShrink: 0 }}>
                      <PosCustomIcon
                        image={ele?.foodType === "VEG" ? vagIcon : nonVegIcon}
                        height={"14px"}
                        width={"14px"}
                      />
                    </div>
                    <div className="compItemNameBadgeCol">
                      <div className="compItemNameBadgeRow">
                        <p className="compProductName" title={ele?.productName}>
                          {ele?.productName}{" "}
                          <span className="compPrdocutQty">x{ele?.quantity}</span>
                        </p>
                      </div>

                      {/* Variant name line */}
                      {ele?.variantName && (
                        <p className="compItemVariantLine">{ele?.variantName}</p>
                      )}

                      {/* Add-on products line */}
                      {ele?.addonProduct?.length > 0 && (
                        <p className="compItemAddonLine">
                          {ele?.addonProduct?.map((a, i) => {
                            const qty = a?.quantity || a?.qty;
                            return (
                              <span key={i}>
                                {i > 0 && ", "}
                                {a?.itemName}
                                {qty && qty > 0 && (
                                  <span style={{ fontWeight: 600 }}> (x{qty})</span>
                                )}
                              </span>
                            );
                          })}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="compQuantityContainer">
                    <button
                      className={`compQuantityButton ${!isSelected && "disabled"}`}
                      onClick={() => isSelected && handleDecreaseQty(ele.cartProductId)}
                      disabled={!isSelected}
                    >
                      <PosCustomIcon
                        icon={<MinusIcon />}
                        height={"12px"}
                        width={"12px"}
                        color={isSelected ? "var(--blue-b0)" : "var(--blue-b2)"}
                      />
                    </button>
                    <input
                      className="compFormInput"
                      type="number"
                      value={complimentaryQuantities[ele?.cartProductId]}
                      onChange={(e) =>
                        isSelected && handleQuantityInput(e, ele?.cartProductId, ele?.quantity)
                      }
                      style={{
                        width: `${String(complimentaryQuantities[ele?.cartProductId] || 1).length + 0.5}ch`,
                        minWidth: "1.5ch",
                      }}
                    />
                    <button
                      className={`compQuantityButton ${!isSelected && "disabled"}`}
                      onClick={() =>
                        isSelected && handleIncreaseQty(ele?.cartProductId, ele?.quantity)
                      }
                      disabled={!isSelected}
                    >
                      <PosCustomIcon
                        icon={<PlusIcon />}
                        height={"12px"}
                        width={"12px"}
                        color={isSelected ? "var(--blue-b0)" : "var(--blue-b2)"}
                      />
                    </button>
                  </div>
                </div>
                <div className="compLowerContainer">
                  <div className="compPriceContainer">
                    <p>
                      {getStoreCurrencySymbol()}
                      {(
                        (ele?.mrpTotal / ele?.quantity) *
                        (complimentaryQuantities[ele?.cartProductId] || 1)
                      ).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ComplimentaryProductList;
